import type { ViewpointVector3 } from '../generate-jig.types'
import { buildMeshNameVariants, type ConventionPartLike } from '../viewpoint/coordinate-convention'
import { decodeRecoveredBoundsFromBuffer } from './draco-bounds-decoder'
import type { ErrorMessage, RecoveredBoundsMessage } from './draco.worker'

export interface RecoveredGeometrySubObject {
  name: string
  materialName: string | null
  center: ViewpointVector3
  size: ViewpointVector3
}

export interface MemeGeometryRecoveryResult {
  source: 'meme-geometry'
  modelUri: string
  subObjects: RecoveredGeometrySubObject[]
}

export interface MatchedRecoveredPartBounds {
  partIndex: number
  referenceMeshName: string
  viewerCenter: ViewpointVector3
  viewerSize: ViewpointVector3
}

export type RecoveryBufferFetch = (modelUri: string) => Promise<ArrayBuffer | null>
export type DecodeBoundsFn = (buffer: ArrayBuffer) => Promise<RecoveredGeometrySubObject[]>

export interface MemeGeometryRecoveryServiceOptions {
  fetchBufferFn?: RecoveryBufferFetch
  decodeBoundsFn?: DecodeBoundsFn
  decodeFallbackFn?: DecodeBoundsFn | null
  workerDecodeTimeoutMs?: number
  decodeTimeoutMs?: number
}

const DEFAULT_DECODE_TIMEOUT_MS = 30000
const DEFAULT_WORKER_DECODE_TIMEOUT_MS = 5000

let worker: Worker | null = null
let nextRequestId = 0
const pendingRequests = new Map<number, {
  resolve: (subObjects: RecoveredGeometrySubObject[]) => void
  reject: (error: Error) => void
}>()

function formatBytes(byteLength: number): string {
  if (!byteLength || byteLength <= 0) {
    return '0B'
  }
  if (byteLength < 1024) {
    return byteLength + 'B'
  }
  if (byteLength < 1024 * 1024) {
    return (byteLength / 1024).toFixed(1) + 'KB'
  }
  return (byteLength / (1024 * 1024)).toFixed(2) + 'MB'
}

function normalizeModelUri(modelUri: string): string {
  try {
    const normalizedUrl = new URL(modelUri, 'https://api.jig.space')
    return normalizedUrl.pathname + normalizedUrl.search + normalizedUrl.hash
  } catch (_error) {
    return modelUri
  }
}

function isMemeUri(modelUri: string): boolean {
  return /\.meme(?:$|[?#])/i.test(modelUri)
}

function getRuntimeOrigin(): string {
  const currentLocation = globalThis.location
  if (currentLocation && typeof currentLocation.origin === 'string' && currentLocation.origin !== 'null') {
    return currentLocation.origin
  }
  return ''
}

function decodeBoundsOnMainThread(buffer: ArrayBuffer): Promise<RecoveredGeometrySubObject[]> {
  const origin = getRuntimeOrigin()
  return decodeRecoveredBoundsFromBuffer(buffer, {
    fetchFn: fetch.bind(globalThis),
    origin: origin,
    onProgress: function (stage: string, detail?: string) {
      // eslint-disable-next-line no-console
      console.log(
        '[draco-main] ' + stage
        + (detail ? ' — ' + detail : '')
        + ' origin=' + (origin || '(none)')
      )
    },
  })
}

async function getWorker(): Promise<Worker> {
  if (typeof Worker === 'undefined') {
    throw new Error('Web Workers unavailable')
  }

  if (worker) {
    return worker
  }

  const factoryModule = await import(/* webpackChunkName: "draco-worker" */ './create-draco-worker')
  worker = factoryModule.createDracoWorker()

  worker.onmessage = function (event: MessageEvent<RecoveredBoundsMessage | ErrorMessage | any>) {
    if (event.data && event.data.type === 'bootstrap-progress') {
      // eslint-disable-next-line no-console
      console.log(
        '[draco-worker] ' + event.data.stage
        + (event.data.detail ? ' — ' + event.data.detail : '')
        + ' origin=' + event.data.origin
      )
      return
    }

    const pending = pendingRequests.get(event.data.id)
    if (!pending) {
      return
    }

    pendingRequests.delete(event.data.id)

    if (event.data.type === 'decoded') {
      pending.resolve(event.data.subObjects.map(function (subObject: any) {
        return {
          name: subObject.name,
          materialName: subObject.materialName,
          center: subObject.center,
          size: subObject.size,
        }
      }))
    } else {
      pending.reject(new Error(event.data.message))
    }
  }

  worker.onerror = function (event) {
    // eslint-disable-next-line no-console
    console.error('[draco-worker] onerror fired:', event.message || '(no message)', 'at', event.filename + ':' + event.lineno)
    pendingRequests.forEach(function (pending, id) {
      pending.reject(new Error('Worker error: ' + (event.message || 'unknown')))
      pendingRequests.delete(id)
    })
  }

  return worker
}

function decodeBoundsInWorker(buffer: ArrayBuffer): Promise<RecoveredGeometrySubObject[]> {
  return new Promise(function (resolve, reject) {
    const id = nextRequestId++
    pendingRequests.set(id, { resolve: resolve, reject: reject })
    getWorker()
      .then(function (workerInstance) {
        workerInstance.postMessage({
          type: 'decode',
          id: id,
          buffer: buffer,
          appOrigin: getRuntimeOrigin(),
        }, [buffer])
      })
      .catch(function (error: unknown) {
        pendingRequests.delete(id)
        reject(error instanceof Error ? error : new Error(String(error)))
      })
  })
}

/**
 * Matches scene parts to recovered sub-object bounds by comparing normalized mesh
 * name variants. Returns null if any bounded part cannot be uniquely matched.
 */
export function matchRecoveredBoundsToParts(
  parts: ConventionPartLike[],
  recoveredSubObjects: RecoveredGeometrySubObject[]
): MatchedRecoveredPartBounds[] | null {
  const boundedParts: Array<{ part: ConventionPartLike; partIndex: number }> = []
  parts.forEach(function (part, partIndex) {
    if (part.center && part.size) {
      boundedParts.push({ part: part, partIndex: partIndex })
    }
  })

  if (boundedParts.length === 0) {
    return []
  }

  const lookup = new Map<string, Array<{ index: number; subObject: RecoveredGeometrySubObject }>>()
  recoveredSubObjects.forEach(function (subObject, index) {
    buildMeshNameVariants(subObject.name).forEach(function (variant) {
      const matches = lookup.get(variant) || []
      matches.push({ index: index, subObject: subObject })
      lookup.set(variant, matches)
    })
  })

  const usedIndices = new Set<number>()
  const matches: MatchedRecoveredPartBounds[] = []

  for (const entry of boundedParts) {
    const variantSet = new Set<string>()
    buildMeshNameVariants(entry.part.name).forEach(function (variant) { variantSet.add(variant) })
    buildMeshNameVariants(entry.part.objectName).forEach(function (variant) { variantSet.add(variant) })
    const variants = Array.from(variantSet)

    let matchedEntry: { index: number; subObject: RecoveredGeometrySubObject } | null = null
    for (const variant of variants) {
      const candidates = lookup.get(variant)
      if (!candidates || candidates.length === 0) {
        continue
      }

      const unmatchedCandidate = candidates.find(function (candidate) {
        return !usedIndices.has(candidate.index)
      })
      if (!unmatchedCandidate) {
        continue
      }

      matchedEntry = unmatchedCandidate
      break
    }

    if (!matchedEntry) {
      return null
    }

    usedIndices.add(matchedEntry.index)
    matches.push({
      partIndex: entry.partIndex,
      referenceMeshName: matchedEntry.subObject.name,
      viewerCenter: matchedEntry.subObject.center,
      viewerSize: matchedEntry.subObject.size,
    })
  }

  return matches
}

/**
 * Fetches .meme geometry once per model URI and decodes per-sub-object viewer-space
 * bounds via a Draco web worker. Callers supply a buffer-fetch function so the
 * service stays decoupled from authentication/transport details.
 */
export class MemeGeometryRecoveryService {
  private readonly cache = new Map<string, Promise<MemeGeometryRecoveryResult | null>>()
  private readonly fetchBufferFn: RecoveryBufferFetch | null
  private readonly decodeBoundsFn: DecodeBoundsFn
  private readonly decodeFallbackFn: DecodeBoundsFn | null
  private readonly workerDecodeTimeoutMs: number
  private readonly decodeTimeoutMs: number

  constructor(options: MemeGeometryRecoveryServiceOptions = {}) {
    this.fetchBufferFn = options.fetchBufferFn || null
    this.decodeBoundsFn = options.decodeBoundsFn || decodeBoundsInWorker
    this.decodeFallbackFn = options.decodeFallbackFn === undefined ? decodeBoundsOnMainThread : options.decodeFallbackFn
    this.workerDecodeTimeoutMs = options.workerDecodeTimeoutMs && options.workerDecodeTimeoutMs > 0
      ? options.workerDecodeTimeoutMs
      : DEFAULT_WORKER_DECODE_TIMEOUT_MS
    this.decodeTimeoutMs = options.decodeTimeoutMs && options.decodeTimeoutMs > 0
      ? options.decodeTimeoutMs
      : DEFAULT_DECODE_TIMEOUT_MS
  }

  recoverBoundsForModelUri(modelUri?: string | null): Promise<MemeGeometryRecoveryResult | null> {
    if (!modelUri) {
      return Promise.resolve(null)
    }

    const normalizedModelUri = normalizeModelUri(modelUri)
    if (!isMemeUri(normalizedModelUri)) {
      return Promise.resolve(null)
    }

    const existing = this.cache.get(normalizedModelUri)
    if (existing) {
      return existing
    }

    const recoveryPromise = this.fetchAndDecode(normalizedModelUri)
    this.cache.set(normalizedModelUri, recoveryPromise)
    return recoveryPromise
  }

  private async fetchAndDecode(modelUri: string): Promise<MemeGeometryRecoveryResult | null> {
    if (!this.fetchBufferFn) {
      return null
    }

    const startedAt = Date.now()
    let bufferSize = 0

    try {
      const buffer = await this.fetchBufferFn(modelUri)
      if (!buffer) {
        return null
      }
      bufferSize = buffer.byteLength

      const subObjects = await this.decodeBounds(buffer)
      if (!subObjects.length) {
        // eslint-disable-next-line no-console
        console.warn(
          'MemeGeometryRecovery: no sub-objects recovered from ' + modelUri
          + ' (buffer=' + formatBytes(bufferSize)
          + ', elapsed=' + (Date.now() - startedAt) + 'ms)'
        )
        return null
      }

      return {
        source: 'meme-geometry',
        modelUri: modelUri,
        subObjects: subObjects,
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'MemeGeometryRecovery: failed for ' + modelUri
        + ' (buffer=' + formatBytes(bufferSize)
        + ', elapsed=' + (Date.now() - startedAt) + 'ms)'
        + ':',
        error
      )
      return null
    }
  }

  private async decodeBounds(buffer: ArrayBuffer): Promise<RecoveredGeometrySubObject[]> {
    const primaryBuffer = this.decodeFallbackFn ? buffer.slice(0) : buffer

    try {
      return await this.withTimeout(
        this.decodeBoundsFn(primaryBuffer),
        this.decodeFallbackFn ? this.workerDecodeTimeoutMs : this.decodeTimeoutMs
      )
    } catch (error) {
      if (!this.decodeFallbackFn) {
        throw error
      }

      // eslint-disable-next-line no-console
      console.warn('[recovery] worker decode failed, retrying on main thread:', error)
      return this.withTimeout(this.decodeFallbackFn(buffer), this.decodeTimeoutMs)
    }
  }

  private withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    return new Promise<T>(function (resolve: (value: T | PromiseLike<T>) => void, reject: (reason?: any) => void) {
      const timeoutId = globalThis.setTimeout(function () {
        reject(new Error('Decode timed out after ' + timeoutMs + 'ms'))
      }, timeoutMs)

      promise.then(function (value) {
        globalThis.clearTimeout(timeoutId)
        resolve(value)
      }).catch(function (error) {
        globalThis.clearTimeout(timeoutId)
        reject(error)
      })
    })
  }
}
